/**
 * `skaile preset apply` — pure core: reverse a `.preset.yaml` snapshot back into
 * a workspace's `skaile.yaml` sections (the inverse of `preset export`).
 *
 * These helpers are I/O- and prompt-free so they can be unit-tested in
 * isolation. The Commander action in `preset-cmd.ts` owns file reads, the
 * interactive prompting for non-secret inputs, the editor writes, and the
 * post-apply install.
 */

import type {
  ConnectorDeclaration,
  McpServerDeclaration,
  SourceEntry,
} from "@skaile/workspaces/core";
import { getDefaultValue, isSecretPlaceholder } from "@skaile/workspaces/library";
import type { Placeholder, PresetManifest, PresetItem } from "@skaile/workspaces/types/manifests";

/** The workspace sections produced by reversing a preset's items. */
export interface WorkspaceItems {
  /** Bare dependency refs, copied verbatim from non-connector/mcp items. */
  dependencies: string[];
  /** Connector declarations rebuilt from `connector:<driver>` items. */
  connectors: ConnectorDeclaration[];
  /** MCP server declarations rebuilt from `mcp:<id>` items. */
  mcpServers: McpServerDeclaration[];
  /** Git sources the deps resolve from — registered before install. */
  sources: SourceEntry[];
  /** Secret placeholder keys the user must export as env vars before running. */
  envVarsToSet: string[];
}

/** A single `${INPUT:KEY}` / `${SECRET:KEY}` token found in a config string. */
const TOKEN_RE = /\$\{(INPUT|SECRET):([^}]+)\}/g;

/** Rejection message for nested preset refs (unsupported by v1 apply). */
export const NESTED_PRESET_UNSUPPORTED = "nested presets are not supported by `preset apply` yet";

/**
 * Resolve one placeholder to the value that lands in `skaile.yaml`.
 *
 * A secret placeholder NEVER yields its raw value — it resolves to an
 * `env:<KEY>` indirection reference so the credential stays out of the file.
 * A non-secret placeholder resolves to the provided literal (or its declared
 * default); a required non-secret placeholder with neither throws.
 *
 * @param placeholder - The placeholder definition from the preset item.
 * @param providedValue - Value supplied via `--input`/prompt, or `undefined`.
 * @returns `{ envRef }` for secrets, `{ value }` for resolved literals.
 * @throws When a required non-secret placeholder has no value and no default.
 */
export function resolvePlaceholderValue(
  placeholder: Placeholder,
  providedValue: string | undefined,
): { value: string } | { envRef: string } {
  if (isSecretPlaceholder(placeholder)) {
    return { envRef: `env:${placeholder.key}` };
  }

  const fallback = getDefaultValue(placeholder);
  const resolved = providedValue ?? (fallback !== undefined ? String(fallback) : undefined);
  if (resolved === undefined) {
    // `required` defaults to true (only an explicit `false` opts out).
    if (placeholder.required !== false) {
      throw new Error(`Placeholder "${placeholder.key}" is required but no value was provided`);
    }
    return { value: "" };
  }
  return { value: resolved };
}

/**
 * Reverse a preset's items into additive `skaile.yaml` sections.
 *
 * Items are classified by **`id` presence** (export writes one on connector/mcp
 * items, never on deps), so a bare `mcp:excel` shorthand stays a dependency.
 * Secret tokens resolve to `env:<KEY>` refs (collected into `envVarsToSet`).
 *
 * @param preset - The validated preset manifest.
 * @param values - Resolved non-secret input values, keyed by placeholder key.
 * @returns The workspace sections plus the secret env-var keys to set.
 * @throws When a required non-secret placeholder is unresolved, or a nested
 *   preset ref is encountered (unsupported by apply).
 */
export function presetItemsToWorkspace(
  preset: PresetManifest,
  values: Record<string, string>,
): WorkspaceItems {
  const dependencies: string[] = [];
  const connectors: ConnectorDeclaration[] = [];
  const mcpServers: McpServerDeclaration[] = [];
  const envVarsToSet = new Set<string>();

  for (const item of preset.items) {
    const ref = item.ref;

    if (ref.startsWith("preset:")) throw new Error(NESTED_PRESET_UNSUPPORTED);

    if (item.id && ref.startsWith("connector:")) {
      const config = resolveItemConfig(item, preset, values, envVarsToSet);
      connectors.push({
        id: item.id,
        driver: ref.slice("connector:".length),
        ...config,
      } as ConnectorDeclaration);
    } else if (item.id && ref.startsWith("mcp:")) {
      const config = resolveItemConfig(item, preset, values, envVarsToSet);
      mcpServers.push({ id: item.id, ...config } as McpServerDeclaration);
    } else {
      dependencies.push(ref);
    }
  }

  const sources: SourceEntry[] = (preset.sources ?? []).map((s) =>
    s.pin ? { url: s.url, pin: s.pin } : { url: s.url },
  );

  return { dependencies, connectors, mcpServers, sources, envVarsToSet: [...envVarsToSet] };
}

/**
 * Walk an item's `config`, substituting interpolation tokens. Secret tokens
 * collapse the whole value to `env:<KEY>` (and record the key); input tokens
 * are replaced with their resolved literal in place.
 */
function resolveItemConfig(
  item: PresetItem,
  preset: PresetManifest,
  values: Record<string, string>,
  envVarsToSet: Set<string>,
): Record<string, unknown> {
  const byKey = placeholderIndex(item, preset);
  const resolve = (key: string): { value: string } | { envRef: string } => {
    const ph = byKey.get(key) ?? { key, type: "text", required: true };
    return resolvePlaceholderValue(ph, values[key]);
  };
  return substituteNode(item.config ?? {}, resolve, envVarsToSet) as Record<string, unknown>;
}

/** Index this item's placeholders (falling back to preset-level inputs) by key. */
function placeholderIndex(item: PresetItem, preset: PresetManifest): Map<string, Placeholder> {
  const map = new Map<string, Placeholder>();
  for (const input of preset.inputs ?? []) map.set(input.key, input as Placeholder);
  for (const ph of item.placeholders ?? []) map.set(ph.key, ph);
  return map;
}

function substituteNode(
  node: unknown,
  resolve: (key: string) => { value: string } | { envRef: string },
  envVarsToSet: Set<string>,
): unknown {
  if (typeof node === "string") return substituteString(node, resolve, envVarsToSet);
  if (Array.isArray(node)) return node.map((el) => substituteNode(el, resolve, envVarsToSet));
  if (node && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) out[k] = substituteNode(v, resolve, envVarsToSet);
    return out;
  }
  return node;
}

function substituteString(
  value: string,
  resolve: (key: string) => { value: string } | { envRef: string },
  envVarsToSet: Set<string>,
): string {
  const tokens = [...value.matchAll(TOKEN_RE)];
  if (tokens.length === 0) return value;

  // A token is a secret when its namespace is `SECRET` (authoritative, matching
  // the library's canonical interpolation) OR its placeholder is `type: secret`.
  // Scan by namespace FIRST (no resolve): otherwise resolving an earlier
  // required-but-missing input would throw and hide the real "sole token" error.
  const secret =
    tokens.find(([, ns]) => ns === "SECRET") ??
    tokens.find(([, , key]) => "envRef" in resolve(key!));
  if (secret) {
    const [full, , key] = secret;
    // A secret resolves to an `env:<KEY>` reference; it MUST be the whole value.
    // Mixing it with other tokens or literal text would drop that text silently,
    // so reject it (mirrors the library's "SECRET must be the sole token").
    if (tokens.length > 1 || full !== value) {
      throw new Error(
        `Secret "${key}" must be the only token in its value — remove the surrounding tokens/text`,
      );
    }
    envVarsToSet.add(key!);
    return `env:${key}`;
  }

  return value.replace(TOKEN_RE, (_m, _ns, key: string) => {
    const resolved = resolve(key);
    return "envRef" in resolved ? resolved.envRef : resolved.value;
  });
}
